




























































  import {
    Filter,
    HierarchicalFilter as HierarchicalFilterModel,
    isHierarchicalFilter
  } from '@empathyco/x-types';
  import { isObject } from '@empathyco/x-utils';
  import Vue from 'vue';
  import { Component, Prop } from 'vue-property-decorator';
  import { dynamicPropsMixin } from '../../../../components/dynamic-props.mixin';
  import { xComponentMixin } from '../../../../components/x-component.mixin';
  import { VueCSSClasses } from '../../../../utils/types';
  import { XEventsTypes } from '../../../../wiring/events.types';
  import { facetsXModule } from '../../x-module';
  import FiltersList from '../lists/filters-list.vue';
  import RenderlessFilter from './renderless-filter.vue';

  /**
   * Renders a hierarchical filter recursively, emitting the needed events when clicked.
   *
   * @public
   */
  @Component({
    name: 'HierarchicalFilter',
    components: { FiltersList, RenderlessFilter },
    mixins: [
      xComponentMixin(facetsXModule),
      dynamicPropsMixin(['childrenFiltersClass', 'filterItemClass'])
    ]
  })
  export default class HierarchicalFilter extends Vue {
    /** The filter data to render. */
    @Prop({ required: true })
    public filter!: HierarchicalFilterModel;

    /** The animation component to use for the children filters. */
    @Prop()
    public childrenAnimation?: Vue | string;

    /**
     * Additional events, with their payload, to emit when the filter is clicked.
     *
     * @public
     */
    @Prop()
    public clickEvents!: Partial<XEventsTypes>;

    /**
     * The {@link XEventsTypes} to emit.
     *
     * @returns The events to emit when clicked.
     * @internal
     */
    protected get _clickEvents(): Partial<XEventsTypes> {
      return {
        UserClickedAHierarchicalFilter: this.filter,
        ...this.clickEvents
      };
    }

    /**
     * Dynamic CSS classes to apply to the component.
     *
     * @returns The dynamic CSS classes to apply to the component.
     * @internal
     */
    protected get cssClasses(): VueCSSClasses {
      return {
        'x-hierarchical-filter--is-partially-selected': this.isPartiallySelected,
        'x-hierarchical-filter--is-selected': this.filter.selected,
        'x-facet-filter--is-partially-selected': this.isPartiallySelected
      };
    }

    /**
     * Gets the child filter click events, converting the payload of the events that have a
     * {@link HierarchicalFilter} as payload to the corresponding child filter.
     *
     * @param childFilter - The child filter.
     * @returns The events to emit when clicking a child.
     * @internal
     */
    protected getChildFilterClickEvents(
      childFilter: HierarchicalFilterModel
    ): Partial<XEventsTypes> {
      return Object.entries(this._clickEvents).reduce((clickEvents, [event, payload]) => {
        return {
          ...clickEvents,
          [event]:
            isObject(payload) &&
            isHierarchicalFilter(payload as unknown as Filter) &&
            childFilter !== (payload as unknown as HierarchicalFilterModel)
              ? childFilter
              : payload
        };
      }, {} as Partial<XEventsTypes>);
    }

    /**
     * Returns if the filter is partially selected, which means having more than one child filter
     * selected, but not every of them, or having at least one child filter partially selected.
     *
     * @returns True if the filter is partially selected. False otherwise.
     * @internal
     */
    protected get isPartiallySelected(): boolean {
      return this.isFilterPartiallySelected(this.filter);
    }

    /**
     * List of filters to render, in case that the children's array
     * is empty it will return an empty array instead of inject the ones from the parent.
     *
     * @returns A list of filters.
     * @internal
     */
    protected get renderedChildrenFilters(): Filter[] {
      return this.filter.children ?? [];
    }

    protected isFilterPartiallySelected(filter: HierarchicalFilterModel): boolean {
      const selectedChildren = filter.children?.filter(filter => filter.selected);
      const filterChildrenLength = filter.children?.length ?? 0;
      return (
        !!selectedChildren &&
        ((selectedChildren.length > 0 && selectedChildren.length < filterChildrenLength) ||
          // eslint-disable-next-line @typescript-eslint/unbound-method
          selectedChildren.some(this.isFilterPartiallySelected))
      );
    }
  }
